import { z } from "zod";
import { createTransform } from "redux-persist";
import { initialState as referenceDashboardInitialState } from "./slices/reference-dashboard";

export function validateOrDefault<T>(
    schema: z.ZodType<T>,
    fallback: T,
    data: unknown,
): T {
    const result = schema.safeParse(data);
    return result.success ? result.data : fallback;
}

export const sectionVisibilitySchema = z.object({
    overdue: z.boolean(),
    today: z.boolean(),
    tomorrow: z.boolean(),
    future: z.boolean(),
    none: z.boolean(),
});

export const designSchema = z.object({
    theme: z.enum(["light", "dark", "system"]),
    sidebarState: z.enum(["expanded", "collapsed"]),
});

const advancedSearchConditionSchema: z.ZodType<unknown> = z.object({
    nodeType: z.enum(["And", "Or", "FieldCondition", "RelationCondition"]),
    children: z
        .lazy(() => z.array(advancedSearchConditionSchema))
        .nullable()
        .optional(),
    fieldName: z.string().nullable().optional(),
    operator: z.string().nullable().optional(),
    textValue: z.string().nullable().optional(),
    numberValue: z.number().nullable().optional(),
    dateValue: z.string().nullable().optional(),
    calendarDateValue: z.string().nullable().optional(),
    promptId: z.string().nullable().optional(),
    questionId: z.string().nullable().optional(),
    relativeDate: z.unknown().nullable().optional(),
});

const searchGeoParamsSchema = z.object({
    address: z.string().nullable().optional(),
    bounds: z
        .object({
            northWest: z.object({
                latitude: z.number(),
                longitude: z.number(),
            }),
            southEast: z.object({
                latitude: z.number(),
                longitude: z.number(),
            }),
        })
        .nullable()
        .optional(),
    city: z.string().nullable().optional(),
    coordinates: z
        .object({ latitude: z.number(), longitude: z.number() })
        .nullable()
        .optional(),
    county: z.string().nullable().optional(),
    distance: z.number().nullable().optional(),
    matchType: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    zip: z.string().nullable().optional(),
});

export const referenceFilterSchema = z.object({
    search: z.string(),
    orgTypes: z.array(z.string()),
    communityTypes: z.array(z.string()),
    contactCondition: advancedSearchConditionSchema.nullable(),
    businessCondition: advancedSearchConditionSchema.nullable(),
    communityCondition: advancedSearchConditionSchema.nullable(),
    assignedUserId: z.string(),
    geoParams: searchGeoParamsSchema.nullable(),
});

const searchRequestSchema = z.object({
    page: z.number(),
    perPage: z.number(),
    search: z.string(),
    sort: z.string(),
    sortDirection: z.string(),
});

const referenceDashboardSchema = z.object({
    persistMode: z.enum(["on", "off"]),
    searchFilter: referenceFilterSchema,
    contactSearchRequest: searchRequestSchema,
    businessSearchRequest: searchRequestSchema,
    communitySearchRequest: searchRequestSchema,
    organizationSearchRequest: searchRequestSchema,
});

const defaultSectionVisibility = {
    overdue: true,
    today: true,
    tomorrow: true,
    future: true,
    none: true,
};

const defaultDesign = {
    theme: "light" as const,
    sidebarState: "expanded" as const,
};

export const taskDashboardValidationTransform = createTransform(
    (inbound: unknown) => inbound,
    (outbound: unknown, key: string) => {
        if (key === "sectionVisibility") {
            return validateOrDefault(
                sectionVisibilitySchema,
                defaultSectionVisibility,
                outbound,
            );
        }
        return outbound;
    },
);

export const designValidationTransform = createTransform(
    (inbound: unknown) => inbound,
    (outbound: unknown, key: string) => {
        if (key === "theme") {
            return validateOrDefault(
                designSchema.shape.theme,
                defaultDesign.theme,
                outbound,
            );
        }
        if (key === "sidebarState") {
            return validateOrDefault(
                designSchema.shape.sidebarState,
                defaultDesign.sidebarState,
                outbound,
            );
        }
        return outbound;
    },
);

export const calendarSchema = z.object({
    viewMode: z.enum(["month", "week", "day"]),
});

const defaultCalendar = {
    viewMode: "week" as const,
};

export const calendarValidationTransform = createTransform(
    (inbound: unknown) => inbound,
    (outbound: unknown, key: string) => {
        if (key === "viewMode") {
            return validateOrDefault(
                calendarSchema.shape.viewMode,
                defaultCalendar.viewMode,
                outbound,
            );
        }
        return outbound;
    },
);

export const referenceDashboardValidationTransform = createTransform(
    (inbound: unknown) => inbound,
    (outbound: unknown) => {
        const result = referenceDashboardSchema.safeParse(outbound);
        if (result.success) {
            return result.data;
        }
        return referenceDashboardInitialState;
    },
    { whitelist: ["referenceDashboard"] },
);
